import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { phoneMask } from '@/components/input/helpers'
import type { T_AuthSecurity } from '@/pages/auth/steps/authorize-ticket'
import { clearPaidCallSession, readPaidCallSession, touchPaidCallStep } from './paid-call-session'


type T_AuthSteps =
  | 'initialScreen'
  | 'outgoingCall'
  | 'incomingCallOrSms'

interface T_AuthStore {
  currentStep: T_AuthSteps
  setCurrentStep: (currentStep: T_AuthSteps) => void

  outgoingPhone: string
  setOutgoingPhone: (outgoingPhone: string) => void

  outgoingMessageNumber: string | null
  setOutgoingMessageNumber: (outgoingMessageNumber: string | null) => void

  userPhone: string
  setUserPhone: (userPhone: string) => void

  ticketId: string | null
  setTicketId: (ticketId: string | null) => void

  authSecurity: T_AuthSecurity | null
  setAuthSecurity: (authSecurity: T_AuthSecurity | null) => void
}

const restoredCall = readPaidCallSession()

export const useAuthStore = create<T_AuthStore>()(
  devtools(
    (set) => ({
      currentStep: restoredCall?.step ?? 'initialScreen',

      setCurrentStep: (currentStep) => set(
        () => {
          if (currentStep === 'initialScreen') {
            clearPaidCallSession()
          } else {
            touchPaidCallStep(currentStep)
          }

          return {
            currentStep,
            ...(currentStep === 'initialScreen' ? { ticketId: null, authSecurity: null } : {}),
          }
        },
        false,
        'setCurrentStep',
      ),


      outgoingPhone: restoredCall?.outgoingPhone ?? '',

      setOutgoingPhone: (outgoingPhone) => set(
        () => ({ outgoingPhone }),
        false,
        'setOutgoingPhone',
      ),


      outgoingMessageNumber: restoredCall?.outgoingMessageNumber ?? null,

      setOutgoingMessageNumber: (outgoingMessageNumber) => set(
        () => ({ outgoingMessageNumber }),
        false,
        'setOutgoingMessageNumber',
      ),


      userPhone: restoredCall?.userPhone || phoneMask,

      setUserPhone: (userPhone) => set(
        () => ({ userPhone }),
        false,
        'setUserPhone',
      ),


      ticketId: restoredCall?.ticketId ?? null,

      setTicketId: (ticketId) => set(
        () => ({ ticketId }),
        false,
        'setTicketId',
      ),


      authSecurity: restoredCall?.authSecurity ?? null,

      setAuthSecurity: (authSecurity) => set(
        () => ({ authSecurity }),
        false,
        'setAuthSecurity',
      ),
    }),

    { store: 'Auth' },
  )
)
